import { Avatar, Button } from '@billda/design-system';
import { PersonIcon } from '@billda/design-system';
import { GangParticipant } from '../../types/gangCreationTypes';

interface GangMemberSelectedListProps {
  participants: GangParticipant[];
  onRemoveUser: (userId: string) => void;
  isLeaderSelection: boolean;
  maxSelections?: number;
  /** IDs of members that exist in the database (from initial gang data) */
  persistedMemberIds?: string[];
  /** When false, hide remove button for persisted members */
  isRemovalEnabled?: boolean;
}

export const GangMemberSelectedList = ({
  participants,
  onRemoveUser,
  isLeaderSelection,
  maxSelections,
  persistedMemberIds = [],
  isRemovalEnabled = true,
}: GangMemberSelectedListProps) => {
  // Determine if a member can be removed:
  // - If feature is enabled, all members can be removed
  // - If feature is disabled, only non-persisted (new) members can be removed
  const canRemoveMember = (userId: string): boolean => {
    if (isRemovalEnabled) {
      return true;
    }
    return !persistedMemberIds.includes(userId);
  };
  return (
    <div className="mt-6">
      <h6 className="type-h6 mb-2">
        Selected {isLeaderSelection ? 'Leaders' : 'Members'}
        {typeof maxSelections === 'number'
          ? ` (${participants.length}/${maxSelections})`
          : ''}
      </h6>

      {participants.length === 0 ? (
        <EmptySelectedState isLeaderSelection={isLeaderSelection} />
      ) : (
        <div className="max-h-[300px] overflow-y-auto rounded-lg border border-border-default bg-surface-canvas p-2">
          {participants.map(participant => (
            <div
              key={participant.userId}
              className="mb-2 rounded-xl border border-border-default bg-surface-default p-4 last:mb-0"
              data-testid={`selected-${participant.userId}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar aria-label={participant.fullName} className="size-8">
                    {participant.fullName.charAt(0).toUpperCase()}
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="type-body-lg font-medium">
                        {participant.fullName}
                      </span>
                      {isLeaderSelection && (
                        <span className="rounded-lg bg-surface-primary px-2 py-0.25 text-[0.75rem] font-semibold text-on-primary">
                          Leader
                        </span>
                      )}
                    </div>
                    <p className="type-body-md text-content-secondary">
                      {participant.email}
                    </p>
                  </div>
                </div>

                {canRemoveMember(participant.userId) && (
                  <Button
                    data-testid={`remove-selected-${participant.userId}`}
                    variant="outlineDanger"
                    size="sm"
                    onClick={() => onRemoveUser(participant.userId)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EmptySelectedState = ({
  isLeaderSelection,
}: {
  isLeaderSelection: boolean;
}) => (
  <div className="rounded-lg border border-dashed border-border-default bg-surface-canvas py-6 text-center text-content-secondary">
    <PersonIcon
      aria-hidden="true"
      className="mx-auto mb-1 size-12 opacity-50"
    />
    <p className="type-body-md">
      No {isLeaderSelection ? 'leaders' : 'members'} selected yet. Search and
      click users above to add them.
    </p>
  </div>
);
